import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatBlock } from "@/components/ui/metric-bar";
import {
  PageHeader,
  PageHeaderBreadcrumb,
  PageHeaderTitle,
  PageHeaderMeta,
  PageHeaderRow,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/lib/api/client";
import type { DashboardPayload } from "@/lib/api/types";
import { formatCurrency } from "@/lib/tms/format";
import { Activity, AlertTriangle, Box, CalendarDays, ExternalLink, PackageCheck, Route, X } from "lucide-react";
import * as React from "react";

const DEMO_CALL_URL = "https://cal.com/yash-shippeek.com/30min";
const DASHBOARD_DEMO_CALL_DISMISSED_KEY = "shippeek-dashboard-demo-call-dismissed";

function shouldShowDashboardDemoCallCard() {
  if (typeof window === "undefined") return true;

  try {
    return localStorage.getItem(DASHBOARD_DEMO_CALL_DISMISSED_KEY) !== "true";
  } catch {
    return true;
  }
}

function MetricCard({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">{label}</p>
          <p className="text-2xl font-semibold tabular-nums">{value}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <span className="grid size-8 shrink-0 place-items-center rounded-lg border-[0.5px] border-border bg-secondary text-muted-foreground">
          <Icon className="size-4" />
        </span>
      </CardContent>
    </Card>
  );
}

function DashboardDemoCallCard({ onDismiss }: { onDismiss: () => void }) {
  return (
    <Card className="border-violet-200 bg-violet-50/70 dark:border-violet-900/60 dark:bg-violet-950/20">
      <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
            <CalendarDays className="size-5" />
          </span>
          <div className="space-y-1">
            <p className="font-semibold">Need help getting live rates?</p>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Schedule a 30-minute Shippeek call and we’ll walk through LTL setup, carrier connections, and your first quote.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button asChild size="sm">
            <a href={DEMO_CALL_URL} target="_blank" rel="noreferrer">
              Schedule call
              <ExternalLink className="size-4" />
            </a>
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss} aria-label="Hide schedule call card">
            <X className="size-4" />
            Hide
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dashboard, setDashboard] = React.useState<DashboardPayload | null>(null);
  const [showDemoCallCard, setShowDemoCallCard] = React.useState(shouldShowDashboardDemoCallCard);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getDashboard();
      setDashboard(data);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load dashboard.");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const metrics = dashboard?.metrics;
  const summaries = dashboard?.summaries;

  const hideDemoCallCard = React.useCallback(() => {
    try {
      localStorage.setItem(DASHBOARD_DEMO_CALL_DISMISSED_KEY, "true");
    } catch {
      // If storage is unavailable, still hide for this session.
    }
    setShowDemoCallCard(false);
  }, []);

  return (
    <div className="space-y-8">
      <PageHeader>
        <PageHeaderBreadcrumb items={[{ label: "Analytics" }, { label: "Dashboard" }]} />
        <PageHeaderRow>
          <div>
            <PageHeaderTitle>TMS Dashboard</PageHeaderTitle>
            <PageHeaderMeta>
              <span>Operational insight from live bookings.</span>
            </PageHeaderMeta>
          </div>
          <PageHeaderActions>
            <Button variant="outline" onClick={() => void load()}>
              <Activity className="size-4" />
              Refresh
            </Button>
          </PageHeaderActions>
        </PageHeaderRow>
      </PageHeader>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {showDemoCallCard ? <DashboardDemoCallCard onDismiss={hideDemoCallCard} /> : null}

      {/* Summary tags */}
      {metrics ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{metrics.totalShipments.toLocaleString()} total shipments</Badge>
          <Badge variant="success">{metrics.delivered30d.toLocaleString()} delivered (30d)</Badge>
          <Badge variant="warning">{metrics.pendingExceptions.toLocaleString()} exceptions / holds</Badge>
        </div>
      ) : null}

      {/* Loading skeleton */}
      {loading ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="space-y-3">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-7 w-16" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : null}

      {/* Metric cards */}
      {metrics ? (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Active Shipments" value={String(metrics.activeShipments)} hint="Open shipment lifecycle" icon={Box} />
          <MetricCard label="In Transit" value={String(metrics.inTransit)} hint="Shipments currently moving" icon={Route} />
          <MetricCard label="Delivered (30d)" value={String(metrics.delivered30d)} hint="Recent completions" icon={PackageCheck} />
          <MetricCard label="Exceptions / Holds" value={String(metrics.pendingExceptions)} hint="Needs intervention" icon={AlertTriangle} />
        </section>
      ) : null}

      {/* Data sections */}
      {summaries ? (
        <section className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Carrier Utilization</CardTitle>
              <p className="text-xs text-muted-foreground">Top carriers by shipment count</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {summaries.topCarriers.length === 0 ? (
                  <li className="text-muted-foreground">No carrier data yet.</li>
                ) : (
                  summaries.topCarriers.map((carrier) => (
                    <li key={`${carrier.code}-${carrier.name}`} className="flex items-center justify-between rounded-lg border-[0.5px] border-border px-3 py-2">
                      <span>{carrier.name} {carrier.code ? `(${carrier.code})` : ""}</span>
                      <span className="font-semibold tabular-nums">{carrier.count}</span>
                    </li>
                  ))
                )}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lane Snapshot</CardTitle>
              <p className="text-xs text-muted-foreground">Top lanes (origin to destination state)</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {summaries.topLanes.length === 0 ? (
                  <li className="text-muted-foreground">No lane data yet.</li>
                ) : (
                  summaries.topLanes.map((lane) => (
                    <li key={lane.lane} className="flex items-center justify-between rounded-lg border-[0.5px] border-border px-3 py-2">
                      <span>{lane.lane}</span>
                      <span className="font-semibold tabular-nums">{lane.count}</span>
                    </li>
                  ))
                )}
              </ul>
            </CardContent>
          </Card>
        </section>
      ) : null}

      {/* Financial snapshot */}
      {summaries ? (
        <Card>
          <CardHeader>
            <CardTitle>Financial Snapshot</CardTitle>
            <p className="text-xs text-muted-foreground">Current shipment-value indicators</p>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border-[0.5px] border-border bg-secondary p-4">
              <StatBlock label="Total Shipment Value" value={formatCurrency(summaries.totalCost)} />
            </div>
            <div className="rounded-lg border-[0.5px] border-border bg-secondary p-4">
              <StatBlock label="Total Line Items" value={summaries.totalItems.toLocaleString()} />
            </div>
            <div className="rounded-lg border-[0.5px] border-border bg-secondary p-4">
              <StatBlock
                label="Avg. Value / Item"
                value={formatCurrency(summaries.totalItems > 0 ? summaries.totalCost / summaries.totalItems : 0)}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
